import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { formatStripeError } from '../lib/stripeErrors';
import { trackPurchase } from '../lib/analytics';
import { InAppBrowserWarning } from '../components/InAppBrowserWarning';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, Loader2, Lock, Ticket } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': { color: '#6b7280' },
      iconColor: '#FF007F',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

const BundlePayForm = ({ token, bundle, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/credits/public/${token}/create-payment-intent`);
        setClientSecret(res.data.client_secret);
      } catch (err) {
        setError(err.response?.data?.detail || 'Could not start payment');
      }
    };
    init();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    setProcessing(true);
    setError(null);
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (stripeError) {
        setError(formatStripeError(stripeError));
        setProcessing(false);
        return;
      }
      if (paymentIntent.status === 'succeeded') {
        trackPurchase(paymentIntent.amount / 100, paymentIntent.id);
        await axios.post(`${API_URL}/api/credits/public/${token}/confirm`);
        onSuccess();
      } else {
        setError(`Payment status: ${paymentIntent.status}`);
        setProcessing(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Payment failed');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <InAppBrowserWarning />
      <div className="bg-[#0a0a0a] border border-[#333] rounded-lg p-4">
        <CardElement options={cardElementOptions} />
      </div>
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300" data-testid="bundle-pay-error">
          {error}
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || processing || !clientSecret}
        className="w-full bg-[#FF007F] hover:bg-[#D6006A] text-white h-12 text-base"
        data-testid="bundle-pay-submit"
      >
        {processing ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
        ) : (
          <><Lock className="w-4 h-4 mr-2" /> Pay £{bundle.amount_total?.toFixed(2)}</>
        )}
      </Button>
      <p className="text-xs text-white/40 text-center">
        Secure payment via Stripe. Your card will be securely saved with Stripe for future top-ups — details are never stored on our servers.
      </p>
    </form>
  );
};

export default function CreditBundlePayPage() {
  const { token } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/credits/public/${token}`);
        setBundle(res.data);
        if (res.data.status === 'paid') setPaid(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'This payment link is invalid.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF007F]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <Card className="bg-[#111] border-[#333] max-w-md w-full">
          <CardContent className="p-8 text-center space-y-2">
            <p className="text-red-400 text-lg">{error}</p>
            <p className="text-white/50 text-sm">
              Please contact the UPA team if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paid) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <Card className="bg-[#111] border-emerald-500/30 max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto" />
            <h1 className="text-2xl font-bold text-white">Payment Complete</h1>
            <p className="text-white/70">
              Your {bundle.credits_total}-class credit bundle for{' '}
              <span className="text-white font-semibold">{bundle.student_name}</span> is ready to use.
            </p>
            <p className="text-sm text-white/50">
              Each time you attend, one credit will be used automatically. We'll let you know when you're running low.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-full mb-4">
            <Ticket className="w-8 h-8 text-[#FF007F]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Pay-as-you-go Credits</h1>
          <p className="text-white/60">Universal Performing Arts</p>
        </div>

        <Card className="bg-[#111] border-[#333] mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/50 text-sm">Student</p>
                <p className="text-white font-semibold text-lg">{bundle.student_name}</p>
              </div>
              <div className="text-right">
                <p className="text-white/50 text-sm">Credits</p>
                <p className="text-[#FF007F] font-bold text-2xl">{bundle.credits_total}</p>
              </div>
            </div>
            <div className="pt-3 border-t border-[#222]">
              <p className="text-white/50 text-sm mb-2">Valid for</p>
              <div className="space-y-1">
                {bundle.allowed_class_names?.map((n, i) => (
                  <p key={i} className="text-white text-sm">• {n}</p>
                ))}
              </div>
            </div>
            <div className="pt-3 border-t border-[#222] flex items-center justify-between">
              <div>
                <p className="text-white/50 text-sm">Total</p>
                <p className="text-white/40 text-xs">
                  {bundle.credits_total} × £{bundle.price_per_credit?.toFixed(2)}
                </p>
              </div>
              <p className="text-3xl font-bold text-white">£{bundle.amount_total?.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-[#FF007F]" />
              <h2 className="text-white font-semibold">Card details</h2>
            </div>
            <Elements stripe={stripePromise}>
              <BundlePayForm
                token={token}
                bundle={bundle}
                onSuccess={() => {
                  setPaid(true);
                  toast.success('Payment successful!');
                }}
              />
            </Elements>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
